/**
 * A protected uploaded file (claim evidence, proof of purchase, etc.) is served
 * by the never-expiring proxy at ``/api/files/<uuid>/view/?t=<token>`` - see the
 * backend ``build_file_view_url`` / TA-23. The API hands the frontend the fully
 * built URL as ``file_url`` / ``thumbnail_url``; there is nothing to sign or
 * fetch client-side, so a bare ``<img src>``, right-click "open in new tab", and
 * "download" all work on the link itself.
 */

/**
 * The lightweight ``thumbnail`` rendition of a file, derived from its full
 * ``file_url``. The capability token signs only the file UUID, so the thumbnail
 * is reached by adding ``&variant=thumbnail`` to the same URL - no separate
 * token. Prefer a payload-supplied ``thumbnail_url`` when one exists; use this
 * only for shapes that carry just the full URL.
 */
export function fileThumbnailUrl(
  fileUrl: string | null | undefined,
): string | undefined {
  if (!fileUrl) return undefined
  if (fileUrl.includes('variant=')) return fileUrl
  return `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}variant=thumbnail`
}

/**
 * Force a native "save as" download of a protected file: the same never-expiring
 * URL with ``&download=1``, which flips the server's ``Content-Disposition`` to
 * ``attachment``. In production the file is served same-origin so the browser
 * saves it with its filename.
 */
export function fileDownloadUrl(
  fileUrl: string | null | undefined,
): string | undefined {
  if (!fileUrl) return undefined
  if (fileUrl.includes('download=')) return fileUrl
  return `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}download=1`
}
